// Settings tab component with PIN-based OAuth

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAniList } from '../context/AniListContext';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { useAdultContent } from '../context/AdultContentContext';
import { OAUTH_CONFIG } from '../config/constants';
import apiService from '../services/apiService';
import './Settings.css';
import LinkSettings from './LinkSettings';

const Settings = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isConnected, username, token, connectAniList, disconnectAniList, disconnectOAuth, isLoading } = useAniList();
    const { titlePreference, setTitlePreference } = useTitlePreference();
    const { hideAdultContent, toggleHideAdultContent } = useAdultContent();
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'fire');
    const [showPinInput, setShowPinInput] = useState(false);
    const [pinCode, setPinCode] = useState('');
    const [oauthError, setOAuthError] = useState('');
    const [oauthSuccess, setOAuthSuccess] = useState('');

    // Suggestion form state
    const [suggCategory, setSuggCategory] = useState('feature');
    const [suggTitle, setSuggTitle] = useState('');
    const [suggDescription, setSuggDescription] = useState('');
    const [suggSubmitting, setSuggSubmitting] = useState(false);
    const [suggSuccess, setSuggSuccess] = useState('');
    const [suggError, setSuggError] = useState('');

    const handleOAuthConnect = () => {
        const authUrl = `${OAUTH_CONFIG.AUTHORIZE_URL}?client_id=${OAUTH_CONFIG.CLIENT_ID}&response_type=${OAUTH_CONFIG.RESPONSE_TYPE}`;
        window.open(authUrl, '_blank');
        setShowPinInput(true);
        setOAuthError('');
        setOAuthSuccess('');
    };

    const handlePinSubmit = async () => {
        if (!pinCode.trim()) {
            setOAuthError('Please enter the access token');
            return;
        }
        setOAuthError('');
        setOAuthSuccess('');
        const result = await connectAniList(pinCode.trim());
        if (result.success) {
            setOAuthSuccess('✓ OAuth connected!');
            setShowPinInput(false);
            setPinCode('');
        } else {
            setOAuthError(result.error || 'Invalid token.');
        }
    };

    const handleDisconnect = () => {
        disconnectAniList();
        localStorage.removeItem('active_anime_source');
        navigate('/login');
    };

    const handleOAuthDisconnect = () => {
        disconnectOAuth();
        setOAuthSuccess('✓ OAuth disconnected!');
        setTimeout(() => setOAuthSuccess(''), 3000);
    };

    const handleThemeChange = async (newTheme) => {
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);
        document.documentElement.setAttribute('data-theme', newTheme);

        // Also save to server for cross-browser persistence
        try {
            await apiService.saveSettingToServer('theme', newTheme);
            console.log('📡 Theme preference saved to server');
        } catch (error) {
            console.error('Failed to save theme to server:', error);
        }
    };

    const handleNamePreferenceChange = (preference) => {
        setTitlePreference(preference);
    };

    // Dynamic placeholders based on selected category
    const placeholders = {
        feature: {
            title: 'e.g. Add watchlist sharing with friends',
            description: 'Describe the feature you\'d like to see. What problem does it solve? How should it work?'
        },
        bug: {
            title: 'e.g. Anime list not loading after login',
            description: 'What went wrong? Steps to reproduce: 1) ... 2) ... Expected vs actual behavior.'
        },
        improvement: {
            title: 'e.g. Make the stats page load faster',
            description: 'What existing feature could be better? How would you improve it?'
        },
        other: {
            title: 'e.g. Question about how groups work',
            description: 'Share your thoughts, questions, or anything else about the app.'
        }
    };

    const currentPlaceholder = placeholders[suggCategory] || placeholders.feature;

    const handleSuggestionSubmit = async () => {
        if (!suggTitle.trim()) {
            setSuggError('Please enter a title for your suggestion');
            return;
        }
        if (!suggDescription.trim()) {
            setSuggError('Please describe your suggestion');
            return;
        }

        setSuggSubmitting(true);
        setSuggError('');
        setSuggSuccess('');

        try {
            await apiService.submitSuggestion({
                username: username,
                category: suggCategory,
                title: suggTitle.trim(),
                description: suggDescription.trim()
            });
            setSuggSuccess('✓ Thank you! Your suggestion has been submitted.');
            setSuggTitle('');
            setSuggDescription('');
            setSuggCategory('feature');
            setTimeout(() => setSuggSuccess(''), 5000);
        } catch (error) {
            setSuggError('Failed to submit suggestion. Please try again.');
        } finally {
            setSuggSubmitting(false);
        }
    };

    return (
        <div className="settings-container">
            <h1 className="settings-title">⚙️ Settings</h1>

            {/* Account Section */}
            <div className="settings-section">
                <h2 className="section-title">👤 Account</h2>
                <div className="connection-card">
                    <div className="connection-status">
                        <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}></span>
                        <span className="status-text">{isConnected ? 'Connected' : 'Not Connected'}</span>
                    </div>
                    {isConnected && username && (
                        <div className="connection-info">
                            <span className="info-label">Username:</span>
                            <span className="info-value">{username}</span>
                        </div>
                    )}
                    <div className="connection-actions">
                        <button onClick={handleDisconnect} className="btn-secondary">Disconnect</button>
                    </div>
                </div>
            </div>

            {/* AniList OAuth Section */}
            <div className="settings-section">
                <h2 className="section-title">🔐 Advanced: OAuth Authorization</h2>
                <div className="oauth-card">
                    <div className="oauth-info">
                        <p className="oauth-description"><strong>Status:</strong> {token ? '✓ OAuth Connected' : '✗ Not Authorized'}</p>
                        <p className="oauth-help">OAuth needed for <strong>adding anime to your AniList</strong>.</p>
                        <p className="oauth-note">💡 OAuth provides full read/write access</p>
                    </div>
                    {!token ? (
                        <div className="oauth-connect">
                            <div className="oauth-warning-box">
                                <div className="warning-icon">⚠️</div>
                                <div className="warning-content">
                                    <strong>OAuth Not Connected</strong>
                                    <p>Connect OAuth to enable adding anime and updating statuses on AniList.</p>
                                </div>
                            </div>
                            {!showPinInput ? (
                                <>
                                    <button onClick={handleOAuthConnect} className="btn-oauth">🔗 Connect OAuth</button>
                                    <p className="oauth-small-text">Opens AniList authorization</p>
                                </>
                            ) : (
                                <div className="pin-input-section">
                                    <p className="pin-instruction">
                                        <strong>1.</strong> Authorize as <strong>{username}</strong><br />
                                        <strong>2.</strong> Copy access token<br />
                                        <strong>3.</strong> Paste below
                                    </p>
                                    <div className="pin-input-group">
                                        <input type="text" value={pinCode} onChange={(e) => setPinCode(e.target.value)}
                                            placeholder="Paste token" className="pin-input" autoFocus />
                                        <button onClick={handlePinSubmit} className="btn-primary" disabled={isLoading}>
                                            {isLoading ? 'Verifying...' : 'Submit'}
                                        </button>
                                    </div>
                                    <button onClick={() => { setShowPinInput(false); setPinCode(''); setOAuthError(''); }} className="btn-link">Cancel</button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="oauth-connected">
                            <div className="oauth-success-box">✓ OAuth connected!</div>
                            <button onClick={handleOAuthDisconnect} className="btn-oauth-disconnect">
                                🔓 Disconnect OAuth
                            </button>
                            <p className="oauth-small-text">Keeps your username connected</p>
                        </div>
                    )}
                    {oauthError && <div className="oauth-error">{oauthError}</div>}
                    {oauthSuccess && <div className="oauth-success">{oauthSuccess}</div>}
                </div>
            </div>

            <div className="settings-section">
                <h2 className="section-title">📺 Display Preferences</h2>
                <div className="preference-card">
                    <h3>Anime Title Language</h3>
                    <p className="preference-help">Choose how anime titles are displayed throughout the app</p>
                    <div className="name-preference-selector">
                        <div
                            className={`preference-option ${titlePreference === 'english' ? 'active' : ''}`}
                            onClick={() => handleNamePreferenceChange('english')}
                        >
                            <span className="preference-label">🇬🇧 English</span>
                            <span className="preference-note">Falls back to Romanji if unavailable</span>
                        </div>
                        <div
                            className={`preference-option ${titlePreference === 'romanji' ? 'active' : ''}`}
                            onClick={() => handleNamePreferenceChange('romanji')}
                        >
                            <span className="preference-label">🇯🇵 Romanji</span>
                            <span className="preference-note">Japanese romanized titles</span>
                        </div>
                    </div>
                </div>

                <div className="preference-card" style={{ marginTop: '1rem' }}>
                    <h3>🔞 Content Filter</h3>
                    <p className="preference-help">Control whether adult (18+) content is visible in your anime and manga lists</p>
                    <div className="name-preference-selector">
                        <div
                            className={`preference-option ${hideAdultContent ? 'active' : ''}`}
                            onClick={() => { if (!hideAdultContent) toggleHideAdultContent(); }}
                        >
                            <span className="preference-label">🛡️ Hidden</span>
                            <span className="preference-note">Adult content is filtered out</span>
                        </div>
                        <div
                            className={`preference-option ${!hideAdultContent ? 'active' : ''}`}
                            onClick={() => { if (hideAdultContent) toggleHideAdultContent(); }}
                        >
                            <span className="preference-label">👁️ Visible</span>
                            <span className="preference-note">All content is shown</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="settings-section">
                <h2 className="section-title">🔗 External Site Links</h2>
                <LinkSettings />
            </div>


            <div className="settings-section">
                <h2 className="section-title">🎨 Appearance</h2>
                <div className="theme-selector">
                    <div className={`theme-option ${theme === 'cosmic' ? 'active' : ''}`} onClick={() => handleThemeChange('cosmic')}>
                        <div className="theme-preview cosmic">
                            <div className="theme-dot" style={{ background: '#A855F7' }}></div>
                            <div className="theme-dot" style={{ background: '#EC4899' }}></div>
                        </div>
                        <span className="theme-name">🌌 Cosmic</span>
                    </div>
                    <div className={`theme-option ${theme === 'midnight' ? 'active' : ''}`} onClick={() => handleThemeChange('midnight')}>
                        <div className="theme-preview midnight">
                            <div className="theme-dot" style={{ background: '#3B82F6' }}></div>
                            <div className="theme-dot" style={{ background: '#60A5FA' }}></div>
                        </div>
                        <span className="theme-name">🌑 Midnight</span>
                    </div>
                    <div className={`theme-option ${theme === 'fire' ? 'active' : ''}`} onClick={() => handleThemeChange('fire')}>
                        <div className="theme-preview fire">
                            <div className="theme-dot" style={{ background: '#E45F3A' }}></div>
                            <div className="theme-dot" style={{ background: '#F97316' }}></div>
                        </div>
                        <span className="theme-name">🔥 Fire</span>
                    </div>
                </div>
            </div>

            {/* Feedback & Suggestions Section */}
            <div className="settings-section">
                <h2 className="section-title">💡 Feedback & Suggestions</h2>
                <div className="suggestion-card">
                    <p className="suggestion-intro">Have an idea for a new feature, found a bug, or want something improved? Let me know!</p>

                    <div className="suggestion-form">
                        <div className="suggestion-field">
                            <label className="suggestion-label">Category</label>
                            <div className="suggestion-category-selector">
                                {[
                                    { value: 'feature', label: '✨ Feature Request' },
                                    { value: 'bug', label: '🐛 Bug Report' },
                                    { value: 'improvement', label: '🔧 Improvement' },
                                    { value: 'other', label: '💬 Other' }
                                ].map(cat => (
                                    <div
                                        key={cat.value}
                                        className={`suggestion-category-option ${suggCategory === cat.value ? 'active' : ''}`}
                                        onClick={() => setSuggCategory(cat.value)}
                                    >
                                        {cat.label}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="suggestion-field">
                            <label className="suggestion-label">Title</label>
                            <input
                                type="text"
                                value={suggTitle}
                                onChange={(e) => setSuggTitle(e.target.value)}
                                placeholder={currentPlaceholder.title}
                                className="suggestion-input"
                                maxLength={200}
                            />
                        </div>

                        <div className="suggestion-field">
                            <label className="suggestion-label">Description</label>
                            <textarea
                                value={suggDescription}
                                onChange={(e) => setSuggDescription(e.target.value)}
                                placeholder={currentPlaceholder.description}
                                className="suggestion-textarea"
                                rows={4}
                                maxLength={2000}
                            />
                            <span className="suggestion-char-count">{suggDescription.length}/2000</span>
                        </div>

                        <button
                            onClick={handleSuggestionSubmit}
                            className="btn-suggestion-submit"
                            disabled={suggSubmitting || !suggTitle.trim() || !suggDescription.trim()}
                        >
                            {suggSubmitting ? '📤 Submitting...' : '📮 Submit Suggestion'}
                        </button>

                        {suggSuccess && <div className="suggestion-success">{suggSuccess}</div>}
                        {suggError && <div className="suggestion-error">{suggError}</div>}
                    </div>
                </div>
            </div>

            {/* Data Sources & Attribution */}
            <div className="settings-section">
                <h2 className="section-title">📋 Data Sources & Attribution</h2>
                <div className="preference-card">
                    <div className="attribution-list">
                        <div className="attribution-item">
                            <span className="attribution-icon">📊</span>
                            <div className="attribution-info">
                                <strong>AniList</strong>
                                <p className="attribution-desc">Anime & manga list data, user profiles, and media information</p>
                                <a href="https://anilist.co" target="_blank" rel="noopener noreferrer" className="attribution-link">anilist.co</a>
                            </div>
                        </div>
                        <div className="attribution-item">
                            <span className="attribution-icon">📖</span>
                            <div className="attribution-info">
                                <strong>MangaUpdates</strong>
                                <p className="attribution-desc">Latest released chapter data for ongoing manga, manhua & manhwa</p>
                                <a href="https://www.mangaupdates.com" target="_blank" rel="noopener noreferrer" className="attribution-link">mangaupdates.com</a>
                            </div>
                        </div>
                        <div className="attribution-item">
                            <span className="attribution-icon">🔖</span>
                            <div className="attribution-info">
                                <strong>Jikan / MyAnimeList</strong>
                                <p className="attribution-desc">Fallback chapter & volume counts for completed and hiatus manga</p>
                                <a href="https://jikan.moe" target="_blank" rel="noopener noreferrer" className="attribution-link">jikan.moe</a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;
